import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Loader2, Send, Plug } from 'lucide-react';
import { toast } from 'sonner';

const PLATFORM_LABELS = {
  linkedin: 'LinkedIn', instagram: 'Instagram', facebook: 'Facebook',
  youtube: 'YouTube', tiktok: 'TikTok', bluesky: 'Bluesky', twitter: 'X',
};

/** Publish a standalone Video Studio clip as its own social post. Lets the user
 *  pick specific connected accounts/pages per platform (like Compose), then
 *  draft / publish-now / schedule. */
export const PostVideoDialog = ({ project, open, onClose, onPosted }) => {
  const { api } = useAuth();
  const [caption, setCaption] = useState('');
  const [targetsByPlatform, setTargetsByPlatform] = useState({}); // {platform:[{account_id,account_name,kind,client_id}]}
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [selectedTargets, setSelectedTargets] = useState({});     // {platform:[account_id]}
  const [mode, setMode] = useState('draft');
  const [scheduleAt, setScheduleAt] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCaption(''); setSelectedPlatforms([]); setSelectedTargets({}); setMode('draft'); setScheduleAt('');
    api.get('/connections/targets')
      .then((r) => {
        const grouped = {};
        (r.data?.targets || []).forEach((t) => { (grouped[t.platform] = grouped[t.platform] || []).push(t); });
        setTargetsByPlatform(grouped);
      })
      .catch(() => setTargetsByPlatform({}));
  }, [open, api]);

  const platformKeys = Object.keys(targetsByPlatform);

  const togglePlatform = (p) => {
    setSelectedPlatforms((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
    setSelectedTargets((prev) => {
      if (prev[p]?.length) return prev;
      const opts = targetsByPlatform[p] || [];
      return opts.length ? { ...prev, [p]: [opts[0].account_id] } : prev;
    });
  };

  const toggleTarget = (p, accountId) => {
    setSelectedTargets((prev) => {
      const cur = prev[p] || [];
      const next = cur.includes(accountId) ? cur.filter((x) => x !== accountId) : [...cur, accountId];
      return { ...prev, [p]: next };
    });
  };

  const submit = async () => {
    if (!caption.trim()) { toast.error('Add a caption'); return; }
    if (!selectedPlatforms.length) { toast.error('Pick at least one platform'); return; }
    if (mode === 'schedule' && !scheduleAt) { toast.error('Pick a date & time'); return; }
    setBusy(true);
    try {
      const targetsPayload = {};
      selectedPlatforms.forEach((p) => {
        const opts = targetsByPlatform[p] || [];
        const selIds = selectedTargets[p] || [];
        const chosen = opts.filter((o) => selIds.includes(o.account_id));
        if (chosen.length) {
          targetsPayload[p] = chosen.map((o) => ({
            account_id: o.account_id, account_name: o.account_name, kind: o.kind, client_id: o.client_id || null,
          }));
        }
      });
      const res = await api.post('/posts', {
        caption: caption.trim(),
        platforms: selectedPlatforms,
        post_type: 'reel',
        media_items: [project.final_media],
        post_title: project.name || 'Video',
        targets: Object.keys(targetsPayload).length ? targetsPayload : undefined,
      });
      const ids = res.data.created_ids || [];
      if (!ids.length) throw new Error('No posts created');

      try { await api.post(`/video/projects/${project.id}/attach`, { campaign_post_id: ids[0] }); } catch (_) { /* non-critical */ }

      if (mode === 'now') {
        const results = await Promise.allSettled(ids.map((id) => api.post(`/posts/${id}/publish-now`)));
        const ok = results.filter((r) => r.status === 'fulfilled').length;
        toast.success(`Published ${ok}/${ids.length} post${ids.length === 1 ? '' : 's'}`);
      } else if (mode === 'schedule') {
        const iso = new Date(scheduleAt).toISOString();
        await Promise.allSettled(ids.map((id) => api.post(`/posts/${id}/schedule`, { scheduled_for: iso })));
        toast.success(`Scheduled ${ids.length} post${ids.length === 1 ? '' : 's'}`);
      } else {
        toast.success(`Created ${ids.length} draft post${ids.length === 1 ? '' : 's'}`);
      }
      onPosted?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create the post');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="post-video-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-indigo-600" /> Post video directly</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">Caption</Label>
            <Textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={3}
              placeholder="Write a caption for your video…"
              className="mt-1.5"
              data-testid="post-video-caption"
            />
          </div>

          <div>
            <Label className="text-xs">Platforms &amp; accounts</Label>
            {platformKeys.length === 0 ? (
              <p className="text-[11px] text-zinc-500 mt-1.5 flex items-center gap-1.5">
                <Plug className="w-3.5 h-3.5" /> No connected accounts. Connect one under Connections first.
              </p>
            ) : (
              <div className="mt-1.5 space-y-2.5" data-testid="post-video-platforms">
                {platformKeys.map((p) => {
                  const opts = targetsByPlatform[p] || [];
                  const active = selectedPlatforms.includes(p);
                  return (
                    <div key={p} className={`rounded-md border p-2.5 ${active ? 'border-indigo-200 bg-indigo-50/40' : 'border-zinc-200'}`}>
                      <button
                        type="button"
                        onClick={() => togglePlatform(p)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? 'bg-indigo-600 text-white border-indigo-600' : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'}`}
                        data-testid={`post-video-platform-${p}`}
                      >
                        {PLATFORM_LABELS[p] || p} <span className="opacity-70">({opts.length})</span>
                      </button>
                      {active && opts.length > 0 && (
                        <div className="mt-2 space-y-1 pl-1">
                          {opts.map((o) => {
                            const checked = (selectedTargets[p] || []).includes(o.account_id);
                            return (
                              <label key={o.account_id} className="flex items-center gap-2 text-[11px] text-zinc-700 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleTarget(p, o.account_id)}
                                  className="accent-indigo-600"
                                  data-testid={`post-video-target-${p}-${o.account_id}`}
                                />
                                <span className="truncate">{o.account_name}{o.kind ? ` · ${o.kind}` : ''}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">When</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="mt-1.5" data-testid="post-video-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Save as draft</SelectItem>
                  <SelectItem value="now">Publish now</SelectItem>
                  <SelectItem value="schedule">Schedule</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {mode === 'schedule' && (
              <div>
                <Label className="text-xs">Date &amp; time</Label>
                <input
                  type="datetime-local"
                  value={scheduleAt}
                  onChange={(e) => setScheduleAt(e.target.value)}
                  className="mt-1.5 w-full border border-zinc-200 rounded-md px-2 py-1.5 text-sm"
                  data-testid="post-video-schedule-at"
                />
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={submit} data-testid="post-video-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
            {mode === 'now' ? 'Publish' : mode === 'schedule' ? 'Schedule' : 'Create draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PostVideoDialog;
